import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  AutoAwesome,
  Email,
  Phone,
  Business,
  Public,
  FolderOutlined,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clientService } from '../services/clientService';
import { projectService } from '../services/projectService';
import { useAuth } from '../contexts/AuthContext';
import { useProjectWorkflow } from '../contexts/ProjectWorkflowContext';
import ProjectForm from '../components/projects/ProjectForm';
import StatusChip from '../components/common/StatusChip';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { CreateProjectData, Project, UpdateProjectData } from '../types';
import {
  completeProjectCreation,
} from '../utils/workflowPanelCache';
import '../components/Layout/workflowLayout.css';

const ClientDetailPage: React.FC = () => {
  const { clientId = '' } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { isAgency, isSuperAdmin, hasPermission } = useAuth();
  const { focusClientInPanel, selectProject } = useProjectWorkflow();
  const [projectFormOpen, setProjectFormOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [showFirstProjectPrompt, setShowFirstProjectPrompt] = useState(
    () => Boolean((location.state as { promptFirstProject?: boolean } | null)?.promptFirstProject),
  );

  const canAccess = isAgency || isSuperAdmin || hasPermission('clients');

  useEffect(() => {
    if (clientId) {
      focusClientInPanel(clientId);
    }
  }, [clientId, focusClientInPanel]);

  const { data: client, isLoading, error } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => clientService.getClient(clientId),
    enabled: Boolean(clientId) && canAccess,
  });

  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: ['client-projects', clientId],
    queryFn: () => projectService.getProjects({ clientId, limit: 100 }),
    enabled: Boolean(clientId) && canAccess,
  });

  const projects = projectsData?.data || [];

  useEffect(() => {
    if ((location.state as { promptFirstProject?: boolean } | null)?.promptFirstProject) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (projects.length > 0) {
      setShowFirstProjectPrompt(false);
    }
  }, [projects.length]);

  const createProjectMutation = useMutation({
    mutationFn: (data: CreateProjectData) => projectService.createProject(data),
    onSuccess: (project: Project) => {
      setShowFirstProjectPrompt(false);
      completeProjectCreation(queryClient, project, {
        closeDialog: () => setProjectFormOpen(false),
        focusClientInPanel,
        selectProject,
        clientName: client?.name || 'Client',
      });
      void queryClient.invalidateQueries({ queryKey: ['client-projects', clientId] });
      void queryClient.invalidateQueries({ queryKey: ['wf-agency-project-count'] });
    },
  });

  const handleProjectSubmit = async (data: CreateProjectData | UpdateProjectData) => {
    setFormLoading(true);
    try {
      await createProjectMutation.mutateAsync({ ...(data as CreateProjectData), clientId });
    } finally {
      setFormLoading(false);
    }
  };

  if (!canAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You do not have access to this page.</Alert>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="320px">
        <LoadingSpinner />
      </Box>
    );
  }

  if (error || !client) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Client not found.</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/clients')}>
          Back to clients
        </Button>
      </Box>
    );
  }

  const fmtDate = (value?: string) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const openProjectForm = () => {
    setShowFirstProjectPrompt(false);
    setProjectFormOpen(true);
  };

  const shouldShowFirstProjectOverlay =
    showFirstProjectPrompt && !projectsLoading && projects.length === 0;

  return (
    <Box sx={{ p: { xs: 2, md: 3.5 }, display: 'flex', flexDirection: 'column', gap: 3, position: 'relative' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Button
            component={RouterLink}
            to="/clients"
            startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
            sx={{ mb: 1.5, textTransform: 'none', color: 'var(--text-muted)', fontSize: '13px' }}
          >
            Back to clients
          </Button>
          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.5px' }}>
            {client.name}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: 'var(--text-muted)', mt: 0.5 }}>
            Client overview — manage projects and start the link-building workflow.
          </Typography>
        </Box>
        <StatusChip status={client.status} />
      </Box>

      <Grid container spacing={2}>
        {[
          { label: 'Email', value: client.email, icon: <Email sx={{ fontSize: 18 }} /> },
          { label: 'Phone', value: client.phone || '—', icon: <Phone sx={{ fontSize: 18 }} /> },
          { label: 'Company', value: client.company || '—', icon: <Business sx={{ fontSize: 18 }} /> },
          { label: 'Country', value: client.country || '—', icon: <Public sx={{ fontSize: 18 }} /> },
        ].map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item.label}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid var(--border)',
                borderRadius: '12px',
                boxShadow: '0 1px 2px rgba(0,0,0,.04)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'var(--text-muted)' }}>
                {item.icon}
                <Typography sx={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  {item.label}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', wordBreak: 'break-word' }}>
                {item.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: '1px solid var(--border)',
          borderRadius: '12px',
          display: 'flex',
          gap: 3,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            Created
          </Typography>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, mt: 0.5 }}>{fmtDate(client.createdAt)}</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            Projects
          </Typography>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, mt: 0.5 }}>{projects.length}</Typography>
        </Box>
        {isSuperAdmin && client.budget != null && (
          <Box>
            <Typography sx={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              Budget
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, mt: 0.5 }}>
              ${Number(client.budget).toLocaleString()}
            </Typography>
          </Box>
        )}
      </Paper>

      {projects.length === 0 ? (
        <Box className="wf-client-gate" sx={{ border: '1px dashed var(--border)', borderRadius: '16px', background: 'var(--surface)' }}>
          <FolderOutlined sx={{ fontSize: 48, color: 'var(--accent)', mb: 1, opacity: 0.85 }} />
          <h2>Create the first project for {client.name}</h2>
          <p>
            Projects organize sites, orders, and billing for this client. Create one to open the workflow in the sidebar.
          </p>
          <button type="button" className="wf-btn-create wf-btn-create-xl" onClick={openProjectForm}>
            <AutoAwesome sx={{ fontSize: 22 }} />
            Create Project
          </button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>
              Projects
            </Typography>
            <button type="button" className="wf-btn-create" onClick={() => setProjectFormOpen(true)}>
              <AutoAwesome sx={{ fontSize: 14 }} />
              Create Project
            </button>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {projectsLoading ? (
              <CircularProgress size={24} />
            ) : (
              projects.map((project) => (
                <Paper
                  key={project.id}
                  elevation={0}
                  sx={{
                    p: 2,
                    border: '1px solid var(--border)',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    cursor: 'pointer',
                    '&:hover': { borderColor: 'var(--accent)', background: 'rgba(37,99,235,0.03)' },
                  }}
                  onClick={() => {
                    focusClientInPanel(clientId);
                    selectProject(project, client.name, 0);
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{project.projectName}</Typography>
                    <Typography sx={{ fontSize: '12px', color: 'var(--text-muted)', mt: 0.25 }}>
                      Created {fmtDate(project.createdAt)}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={project.isActive ? 'Active' : 'Inactive'}
                    color={project.isActive ? 'success' : 'default'}
                    variant="outlined"
                  />
                </Paper>
              ))
            )}
          </Box>
        </Box>
      )}

      {shouldShowFirstProjectOverlay && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            bgcolor: 'rgba(15, 23, 42, 0.42)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          <Paper
            elevation={12}
            sx={{
              maxWidth: 480,
              width: '100%',
              p: { xs: 3, sm: 4 },
              borderRadius: '20px',
              textAlign: 'center',
              border: '1px solid rgba(37, 99, 235, 0.2)',
              boxShadow: '0 24px 60px rgba(37, 99, 235, 0.18)',
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                mx: 'auto',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #dbeafe, #eff6ff)',
              }}
            >
              <FolderOutlined sx={{ fontSize: 36, color: 'var(--accent)' }} />
            </Box>
            <Typography sx={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-primary)', mb: 1 }}>
              Client created — next step
            </Typography>
            <Typography sx={{ fontSize: '14px', color: 'var(--text-muted)', mb: 3, lineHeight: 1.6 }}>
              <strong>{client.name}</strong> is ready. Create the first project to start exploring sites, building orders, and running the link-building workflow.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AutoAwesome />}
              onClick={openProjectForm}
              sx={{
                py: 1.4,
                px: 3,
                fontSize: '15px',
                fontWeight: 600,
                borderRadius: '12px',
                textTransform: 'none',
                boxShadow: '0 8px 24px rgba(37, 99, 235, 0.35)',
              }}
            >
              Create Project
            </Button>
            <Button
              onClick={() => setShowFirstProjectPrompt(false)}
              sx={{ display: 'block', mx: 'auto', mt: 2, textTransform: 'none', color: 'var(--text-muted)' }}
            >
              I&apos;ll do this later
            </Button>
          </Paper>
        </Box>
      )}

      <ProjectForm
        open={projectFormOpen}
        onClose={() => setProjectFormOpen(false)}
        onSubmit={handleProjectSubmit}
        clients={[{ id: client.id, name: client.name, email: client.email }]}
        loading={formLoading}
        fixedClientId={client.id}
        canCreateClient={false}
      />
    </Box>
  );
};

export default ClientDetailPage;
